import { arbitraryUser } from "@buildwithflux/models/test";
import { guid } from "@buildwithflux/shared";
import { sample } from "fast-check";
import { cloneDeep } from "lodash";
import { BaseUserRepository } from "./BaseUserRepository";
const defaultConfig = {
    preloadedUserData: {},
    createMissingUsersOnGet: false,
};
export class MockUserRepository extends BaseUserRepository {
    userData;
    createMissingUsersOnGet;
    constructor(config = {}) {
        super();
        this.userData = config.preloadedUserData ?? defaultConfig.preloadedUserData;
        this.createMissingUsersOnGet = config.createMissingUsersOnGet ?? defaultConfig.createMissingUsersOnGet;
    }
    /** @inheritDoc */
    delete(user) {
        const existing = this.userData[user.handle];
        if (existing) {
            delete this.userData[user.handle];
            this.subscriptionMgrByUid.notify(existing.uid, { type: "deleted" });
            this.subscriptionMgrByHandle.notify(user.handle, { type: "deleted" });
        }
        return Promise.resolve();
    }
    /** @inheritDoc */
    removeProjectTemplate(handle, documentUid) {
        delete this.userData[handle]?.project_templates?.[documentUid];
        return Promise.resolve();
    }
    /** @inheritDoc */
    getByHandle(handle) {
        const userData = this.userData[handle] ?? this.generateUserIfAllowed({ userHandle: handle });
        return Promise.resolve(userData);
    }
    getByHandlePrefix(handlePrefix, { limit } = { limit: 50 }) {
        return Promise.resolve(Object.entries(this.userData)
            .flatMap(([handle, user]) => (handle.startsWith(handlePrefix) ? [user] : []))
            .slice(0, limit));
    }
    /** @inheritDoc */
    getByUid(userUid) {
        const maybeUser = Object.values(this.userData).find((user) => user.uid === userUid) ?? this.generateUserIfAllowed({ userUid });
        return Promise.resolve(maybeUser);
    }
    /** @inheritDoc */
    getByUids(userUids) {
        return Promise.resolve(Object.fromEntries(Object.entries(this.userData).filter(([k]) => userUids.includes(k))));
    }
    /** @inheritDoc */
    getAllByUid(userUid) {
        return Promise.resolve(Object.values(this.userData).filter((user) => user.uid === userUid));
    }
    /** @inheritDoc */
    getByEmail(email) {
        return Promise.resolve(Object.values(this.userData).filter((user) => user.email === email)?.[0]);
    }
    /** @inheritDoc */
    save(userData) {
        const existing = this.userData[userData.handle];
        this.userData[userData.handle] = cloneDeep(userData);
        this.subscriptionMgrByUid.notify(userData.uid, {
            type: existing ? "updated" : "created",
            data: userData,
        });
        this.subscriptionMgrByHandle.notify(userData.handle, {
            type: existing ? "updated" : "created",
            data: userData,
        });
        return Promise.resolve();
    }
    /** @inheritDoc */
    update(userHandle, updateWith) {
        const existing = this.userData[userHandle];
        if (!existing)
            throw new Error("cannot update non-existing user object");
        const newUser = {
            ...existing,
            ...updateWith,
        };
        this.userData[userHandle] = newUser;
        this.subscriptionMgrByUid.notify(existing.uid, {
            type: existing ? "updated" : "created",
            data: newUser,
        });
        this.subscriptionMgrByHandle.notify(existing.handle, {
            type: existing ? "updated" : "created",
            data: newUser,
        });
        return Promise.resolve();
    }
    /** @inheritDoc */
    updateOrganizationMembershipSentinel(userUid) {
        Object.values(this.userData)
            .filter((user) => user.uid === userUid)
            // eslint-disable-next-line @buildwithflux/no-array-foreach
            .forEach((user) => {
            user.organization_membership_sentinel = guid();
            user.updated_at = Date.now();
        });
        return Promise.resolve();
    }
    /** @inheritDoc */
    updateDocumentDataSentinel(userUid) {
        Object.values(this.userData)
            .filter((user) => user.uid === userUid)
            // eslint-disable-next-line @buildwithflux/no-array-foreach
            .forEach((user) => {
            user.document_data_sentinel = guid();
            user.updated_at = Date.now();
        });
        return Promise.resolve();
    }
    generateUserIfAllowed(userData) {
        if (!this.createMissingUsersOnGet)
            return undefined;
        const newUserData = sample(arbitraryUser(userData), 1)[0];
        if (!newUserData) {
            throw new Error("something went wrong when generating user data");
        }
        return newUserData;
    }
}
//# sourceMappingURL=MockUserRepository.js.map